/**
 * Tipos internos da tela do calendário.
 *
 * Papel: aqui ficam os formatos que o react-hook-form guarda para o dia, a
 * ausência e a visita, e os três valores em branco de onde a criação parte.
 *
 * Os três são derivados dos schemas do contrato com `z.input`, e não escritos à
 * mão, porque é a entrada daqueles schemas que o formulário precisa produzir.
 */
import {
  type novaAusenciaSchema,
  type novaVisitaSchema,
  type novoDiaCalendarioSchema,
} from '@concierge/shared';
import { type z } from 'zod';

/** O dia como o formulário o guarda, antes de o schema normalizar. */
export type DiaNoFormulario = z.input<typeof novoDiaCalendarioSchema>;

/** A ausência como o formulário a guarda. */
export type AusenciaNoFormulario = z.input<typeof novaAusenciaSchema>;

/** A visita como o formulário a guarda. */
export type VisitaNoFormulario = z.input<typeof novaVisitaSchema>;

/**
 * Uma pessoa da escala da casa, no mínimo que a grade e os formulários usam.
 *
 * `id` é o do colaborador, e não o da linha em `calendario_equipe`: é ele que os
 * dias, as ausências e a escala padrão guardam.
 */
export interface PessoaDaEscala {
  id: string;
  nomeCompleto: string;
  apelido: string | null;
}

/**
 * Uma ausência em branco, de onde o formulário de criação parte.
 *
 * Quem abre o formulário troca `colaboradorId` pela primeira pessoa da escala,
 * como a origem faz; vazio, só quando a escala não tem ninguém, e então o
 * formulário não salva.
 */
export const AUSENCIA_EM_BRANCO: AusenciaNoFormulario = {
  colaboradorId: '',
  inicio: '',
  fim: '',
  observacao: '',
};

/**
 * Uma visita em branco, como o formulário de criação começa.
 *
 * `residenciaId` é preenchido por quem monta o formulário, com a casa que a tela
 * está mostrando — a visita é sempre à casa do calendário aberto —, e
 * `visitante` com o primeiro prestador da lista, como a origem faz.
 */
export const VISITA_EM_BRANCO: VisitaNoFormulario = {
  residenciaId: '',
  visitante: '',
  inicio: '',
  fim: '',
  observacao: '',
};
